import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, primaryKey, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { user } from '../auth'
import { Book } from '../content'
import { SentBookCollection } from './sent-book-collection'

export const MyBookCollection = pgTable(
  'my_book_collection',
  {
    id: uuid().primaryKey().defaultRandom(),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    name: text().notNull(),
    description: text(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),

    // LEGACY
    legacyReadingListId: integer('legacy_reading_list_id'),
  },
  (table) => [index('idx_my_book_collection_user').on(table.userId)],
)

export const MyBookCollectionToBook = pgTable(
  'my_book_collection_to_book',
  {
    bookCollectionId: uuid()
      .notNull()
      .references(() => MyBookCollection.id, { onDelete: 'cascade' }),
    bookId: uuid()
      .notNull()
      .references(() => Book.id, { onDelete: 'cascade' }),
    /** Position of the book within the collection, ascending. Set from the client's drag order. */
    sortOrder: integer().notNull().default(0),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    primaryKey({ columns: [table.bookCollectionId, table.bookId] }),
    index('idx_my_book_collection_to_book_collection').on(table.bookCollectionId),
    index('idx_my_book_collection_to_book_book').on(table.bookId),
  ],
)

export const myBookCollectionRelations = relations(MyBookCollection, ({ one, many }) => ({
  user: one(user, {
    fields: [MyBookCollection.userId],
    references: [user.id],
  }),

  myBookCollectionToBooks: many(MyBookCollectionToBook),

  sentBookCollections: many(SentBookCollection),
}))

export const myBookCollectionToBookRelations = relations(MyBookCollectionToBook, ({ one }) => ({
  myBookCollection: one(MyBookCollection, {
    fields: [MyBookCollectionToBook.bookCollectionId],
    references: [MyBookCollection.id],
  }),

  book: one(Book, {
    fields: [MyBookCollectionToBook.bookId],
    references: [Book.id],
  }),
}))

export const InsertMyBookCollectionSchema = createInsertSchema(MyBookCollection).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateMyBookCollectionSchema = createUpdateSchema(MyBookCollection).partial().omit({
  id: true,
  userId: true,
  legacyReadingListId: true,
  createdAt: true,
  updatedAt: true,
})

export const SelectMyBookCollectionSchema = createSelectSchema(MyBookCollection)

export const InsertMyBookCollectionToBookSchema = createInsertSchema(MyBookCollectionToBook).omit({
  createdAt: true,
})

export const UpdateMyBookCollectionToBookSchema = createUpdateSchema(MyBookCollectionToBook).partial().omit({
  bookCollectionId: true,
  bookId: true,
  createdAt: true,
})

export const SelectMyBookCollectionToBookSchema = createSelectSchema(MyBookCollectionToBook)

export type InsertMyBookCollection = typeof MyBookCollection.$inferInsert
export type UpdateMyBookCollection = z.infer<typeof UpdateMyBookCollectionSchema>
export type SelectMyBookCollection = typeof MyBookCollection.$inferSelect
export type InsertMyBookCollectionToBook = typeof MyBookCollectionToBook.$inferInsert
export type UpdateMyBookCollectionToBook = z.infer<typeof UpdateMyBookCollectionToBookSchema>
export type SelectMyBookCollectionToBook = typeof MyBookCollectionToBook.$inferSelect
